import { ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, OnDestroy, ViewEncapsulation } from '@angular/core';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { Layout } from '@src/suite/common/data-models/service/layout';
import { NULL_TIMESPAN } from '@src/suite/common/enums/constants';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { selectEstimatedTimeThreshold, selectRundownTimeEstimate } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { formatDurationLikeMoment, toLuxonLocaleDateString } from '@src/suite/common/utils/date-utils';
import { getHardHitOverUnderText, getOverUnderText, getSegmentTime, updateProgramTime, updateSegmentTime, updateStoryTime } from '@src/suite/common/utils/timing';
import { Duration } from 'luxon';
import { Subject, Subscription, takeUntil, tap, timer } from 'rxjs';

@Component({
    selector: 'st-ro-grid-timing-bar',
    imports: [MatDivider, MatIcon, MatTooltip],
    templateUrl: './st-ro-grid-timing-bar.html',
    styleUrl: './st-ro-grid-timing-bar.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-ro-grid-timing-bar',
    },
})
export class StRoGridTimingBarComponent implements OnDestroy {
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);

    readonly record = input.required<StorytellingRundownRecord>();
    readonly timingObject = linkedSignal<TimingObjectDuration>(() => this.record().TimingObj);
    readonly layout = input<Layout>();
    readonly selectedRows = input<StorytellingStoryRecord[]>();

    languageSettings = this.store.selectSignal(selectI18N);

    timingBarTimer: Subscription = null;
    destroyed$ = new Subject<void>();

    constructor() {
        effect(() => {
            if (this.onAir() && !this.timingBarTimer) {
                this.timingBarTimer = timer(0, 1000)
                    .pipe(
                        tap(() => {
                            const segmentTiming = updateSegmentTime(this.timingObject());
                            const storyTiming = updateStoryTime(this.timingObject());
                            const programTiming = updateProgramTime(this.timingObject());
                            this.timingObject.set({
                                ...this.timingObject(),
                                StoryCountDown: storyTiming.storyCountDown,
                                IsStoryCountDown: storyTiming.isStoryCountDown,
                                SegmentCountDown: segmentTiming.segmentCountDown,
                                SegmentCountUp: segmentTiming.segmentCountUp,
                                IsSegCountDown: segmentTiming.isSegmentCountDown,
                                ProgramCountDown: programTiming.programCountDown,
                                IsROCountDown: programTiming.isProgramCountDown,
                            });
                        }),
                        takeUntil(this.destroyed$)
                    )
                    .subscribe();
            }
            if (!this.onAir() && this.timingBarTimer) {
                this.timingBarTimer.unsubscribe();
                this.timingBarTimer = null;
            }
        });
    }

    readonly timingOverUnder = computed(() => {
        return getOverUnderText(this.timingObject(), this.languageSettings());
    });

    readonly timingHardHitOverUnder = computed(() => {
        return getHardHitOverUnderText(this.timingObject()?.FrontOverUnder, this.languageSettings());
    });

    readonly roLocked = computed(() => {
        return this.record()?.Locked;
    });

    readonly roLockTime = computed(() => {
        return this.record()?.LockTime;
    });

    readonly timingSelection = computed(() => {
        if (!this.selectedRows() || this.selectedRows().length === 0) {
            return null;
        }
        let dur = Duration.fromMillis(0);
        const isRoTimeEstimated = this.store.selectSignal(selectRundownTimeEstimate)();
        const estimatedTimeThreshold = this.store.selectSignal(selectEstimatedTimeThreshold)();
        this.selectedRows().forEach(row => {
            dur = dur.plus(getSegmentTime(row, isRoTimeEstimated, estimatedTimeThreshold));
        });
        return formatDurationLikeMoment(dur);
    });

    readonly hasHardHit = computed(() => {
        const hardHitColumnShown = this.layout()?.columns?.some(col => col.name === PSTFieldNames.HardHitFrontOverUnder || col.name === PSTFieldNames.HardHitBackOverUnder) || false;
        // This is a magic number trust
        const hardHitIsNullTimespan = this.timingObject()?.FrontOverUnder?.as('seconds') === NULL_TIMESPAN.as('seconds');
        return hardHitColumnShown && !hardHitIsNullTimespan;
    });

    readonly roLockedBy = computed(() => {
        // TODO: STAFF NEEDS TO BE IN THE STORE
        const user = this.globalTablesService.Staff[this.record()?.LockedBy]?.DisplayName || this.record()?.LockedBy;
        return this.languageSettings().RDLockInfo.replace('{datetime}', toLuxonLocaleDateString(this.record()?.LockTime)).replace('{user}', user);
    });

    readonly onAir = computed(() => this.record()?.OnAir);

    readonly timingBarLocked = computed(() => this.record()?.TimingLocked);

    readonly timingBarLockedBy = computed(() => {
        if (!this.record()?.TimingLocked || !this.record()?.TimingLockedTime) {
            return '';
        }
        const user = this.globalTablesService.Staff[this.record()?.TimingLockedBy]?.DisplayName || this.record()?.TimingLockedBy;
        return this.languageSettings().RDLockInfo.replace('{datetime}', toLuxonLocaleDateString(this.record()?.TimingLockedTime)).replace('{user}', user);
    });

    readonly roCurrentID = computed(() => this.record()?.CurrentID);

    readonly segmentCountUp = computed(() => {
        return formatDurationLikeMoment(this.timingObject()?.SegmentCountUp);
    });

    readonly segmentCountDown = computed(() => {
        return formatDurationLikeMoment(this.timingObject()?.SegmentCountDown);
    });

    readonly isSegmentCountDown = computed(() => this.timingObject()?.IsSegCountDown);

    readonly storyCountDown = computed(() => {
        return formatDurationLikeMoment(this.timingObject()?.StoryCountDown);
    });

    readonly isStoryCountDown = computed(() => this.timingObject()?.IsStoryCountDown);

    readonly programCountDown = computed(() => {
        return formatDurationLikeMoment(this.timingObject()?.ProgramCountDown);
    });

    readonly isProgramCountDown = computed(() => this.timingObject()?.IsROCountDown);

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
